<template>
  <div class="admin-commission-invoices">
    <b-card>
      <b-card-header class="d-flex justify-content-between align-items-center">
        <h4 class="mb-0">
          <feather-icon
            icon="FileTextIcon"
            size="20"
            class="mr-1"
          />
          Factures de Commission
        </h4>
        <b-button
          variant="primary"
          size="sm"
          @click="showGenerateModal = true"
        >
          <feather-icon
            icon="PlusIcon"
            size="14"
            class="mr-1"
          />
          Générer une Facture
        </b-button>
      </b-card-header>

      <b-card-body>
        <!-- Filters -->
        <b-row class="mb-3">
          <b-col md="4">
            <b-form-group label="Commercial">
              <b-form-select
                v-model="filters.salesperson"
                :options="salespersonOptions"
                @change="loadInvoices"
              />
            </b-form-group>
          </b-col>
          <b-col md="4">
            <b-form-group label="Statut de paiement">
              <b-form-select
                v-model="filters.payment_status"
                :options="statusOptions"
                @change="loadInvoices"
              />
            </b-form-group>
          </b-col>
        </b-row>

        <div
          v-if="loading"
          class="text-center py-5"
        >
          <b-spinner variant="primary" />
        </div>

        <b-table
          v-else
          :items="invoices"
          :fields="fields"
          responsive
          hover
        >
          <template #cell(invoice_number)="data">
            <span class="font-weight-bold">{{ data.item.invoice_number }}</span>
          </template>

          <template #cell(salesperson_name)="data">
            {{ data.item.salesperson_name }}
          </template>

          <template #cell(period)="data">
            {{ formatDate(data.item.period_start) }} - {{ formatDate(data.item.period_end) }}
          </template>

          <template #cell(total_amount)="data">
            <span class="font-weight-bold text-success">
              {{ formatAmount(data.item.total_amount) }} DT
            </span>
          </template>

          <template #cell(payment_status)="data">
            <b-badge :variant="data.item.payment_status === 'paid' ? 'success' : 'warning'">
              {{ data.item.payment_status_display }}
            </b-badge>
          </template>

          <template #cell(actions)="data">
            <b-dropdown
              size="sm"
              variant="outline-secondary"
              no-caret
            >
              <template #button-content>
                <feather-icon
                  icon="MoreVerticalIcon"
                  size="14"
                />
              </template>
              <b-dropdown-item
                v-if="data.item.payment_status === 'unpaid'"
                @click="markAsPaid(data.item)"
              >
                <feather-icon
                  icon="CheckIcon"
                  size="14"
                  class="mr-1"
                />
                Marquer comme payée
              </b-dropdown-item>
              <b-dropdown-item
                v-if="data.item.payment_status === 'paid'"
                @click="markAsUnpaid(data.item)"
              >
                <feather-icon
                  icon="XIcon"
                  size="14"
                  class="mr-1"
                />
                Marquer comme non payée
              </b-dropdown-item>
            </b-dropdown>
          </template>
        </b-table>
      </b-card-body>
    </b-card>

    <!-- Generate Invoice Modal -->
    <b-modal
      v-model="showGenerateModal"
      title="Générer une Facture de Commission"
      hide-footer
      size="lg"
    >
      <b-form @submit.prevent="generateInvoice">
        <b-form-group label="Commercial">
          <b-form-select
            v-model="generateForm.salesperson"
            :options="salespersonOptionsForGenerate"
            required
          />
        </b-form-group>
        <b-row>
          <b-col md="6">
            <b-form-group label="Date de début *">
              <b-form-input
                v-model="generateForm.period_start"
                type="date"
                required
              />
            </b-form-group>
          </b-col>
          <b-col md="6">
            <b-form-group label="Date de fin *">
              <b-form-input
                v-model="generateForm.period_end"
                type="date"
                required
              />
            </b-form-group>
          </b-col>
        </b-row>
        <b-alert
          v-if="generateForm.salesperson"
          show
          variant="info"
          class="mb-3"
        >
          La période doit être couverte par un seul taux de commission.
        </b-alert>

        <div class="d-flex justify-content-end gap-2">
          <b-button
            variant="outline-secondary"
            @click="showGenerateModal = false"
          >
            Annuler
          </b-button>
          <b-button
            type="submit"
            variant="primary"
            :disabled="generating || !generateForm.salesperson || !generateForm.period_start || !generateForm.period_end"
          >
            {{ generating ? 'Génération...' : 'Générer' }}
          </b-button>
        </div>
      </b-form>
    </b-modal>
  </div>
</template>

<script>
import axios from 'axios'
import {
  BCard,
  BCardHeader,
  BCardBody,
  BTable,
  BButton,
  BSpinner,
  BBadge,
  BRow,
  BCol,
  BFormGroup,
  BFormSelect,
  BFormInput,
  BForm,
  BModal,
  BAlert,
  BDropdown,
  BDropdownItem,
} from 'bootstrap-vue'

export default {
  name: 'AdminCommissionInvoices',
  components: {
    BCard,
    BCardHeader,
    BCardBody,
    BTable,
    BButton,
    BSpinner,
    BBadge,
    BRow,
    BCol,
    BFormGroup,
    BFormSelect,
    BFormInput,
    BForm,
    BModal,
    BAlert,
    BDropdown,
    BDropdownItem,
  },
  data() {
    return {
      loading: false,
      generating: false,
      invoices: [],
      salespeople: [],
      showGenerateModal: false,
      filters: {
        salesperson: '',
        payment_status: '',
      },
      generateForm: {
        salesperson: '',
        period_start: '',
        period_end: '',
      },
      statusOptions: [
        { value: '', text: 'Tous les statuts' },
        { value: 'unpaid', text: 'Non payée' },
        { value: 'paid', text: 'Payée' },
      ],
      fields: [
        { key: 'invoice_number', label: 'N° Facture' },
        { key: 'salesperson_name', label: 'Commercial' },
        { key: 'period', label: 'Période' },
        { key: 'total_orders', label: 'Commandes', class: 'text-center' },
        { key: 'total_amount', label: 'Montant', class: 'text-right' },
        { key: 'payment_status', label: 'Statut', class: 'text-center' },
        { key: 'actions', label: '', class: 'text-center' },
      ],
    }
  },
  computed: {
    salespersonOptions() {
      const options = [{ value: '', text: 'Tous les commerciaux' }]
      this.salespeople.forEach(sp => {
        options.push({ value: sp.id, text: sp.username })
      })
      return options
    },
    salespersonOptionsForGenerate() {
      const options = [{ value: '', text: 'Sélectionner un commercial' }]
      this.salespeople.forEach(sp => {
        options.push({ value: sp.id, text: sp.username })
      })
      return options
    },
  },
  mounted() {
    this.loadSalespeople()
    this.loadInvoices()
  },
  methods: {
    async loadSalespeople() {
      try {
        const response = await axios.get('/api/auth/users/', {
          params: { role: 'sales' },
        })
        this.salespeople = response.data.results || response.data
      } catch (error) {
        console.error('Error loading salespeople:', error)
      }
    },
    async loadInvoices() {
      this.loading = true
      try {
        const params = {}
        if (this.filters.salesperson) params.salesperson = this.filters.salesperson
        if (this.filters.payment_status) params.payment_status = this.filters.payment_status
        const response = await axios.get('/api/commissions/commission-invoices/', { params })
        this.invoices = response.data.results || response.data
      } catch (error) {
        console.error('Error loading invoices:', error)
      } finally {
        this.loading = false
      }
    },
    async generateInvoice() {
      if (!this.generateForm.salesperson || !this.generateForm.period_start || !this.generateForm.period_end) {
        return
      }
      if (this.generateForm.period_start > this.generateForm.period_end) {
        this.$bvToast.toast('La date de début doit précéder la date de fin.', {
          title: 'Erreur',
          variant: 'danger',
          solid: true,
        })
        return
      }
      this.generating = true
      try {
        await axios.post('/api/commissions/commission-invoices/generate/', {
          salesperson: this.generateForm.salesperson,
          period_start: this.generateForm.period_start,
          period_end: this.generateForm.period_end,
        })
        this.$bvToast.toast('Facture générée avec succès', {
          title: 'Succès',
          variant: 'success',
          solid: true,
        })
        this.showGenerateModal = false
        this.generateForm = { salesperson: '', period_start: '', period_end: '' }
        this.loadInvoices()
      } catch (error) {
        const message = error.response?.data?.error || error.response?.data?.detail || 'Erreur lors de la génération'
        this.$bvToast.toast(message, {
          title: 'Erreur',
          variant: 'danger',
          solid: true,
        })
      } finally {
        this.generating = false
      }
    },
    async markAsPaid(invoice) {
      try {
        await axios.patch(`/api/commissions/commission-invoices/${invoice.id}/payment-status/`, {
          payment_status: 'paid',
        })
        this.$bvToast.toast('Facture marquée comme payée', {
          title: 'Succès',
          variant: 'success',
          solid: true,
        })
        this.loadInvoices()
      } catch (error) {
        this.$bvToast.toast('Erreur lors de la mise à jour', {
          title: 'Erreur',
          variant: 'danger',
          solid: true,
        })
      }
    },
    async markAsUnpaid(invoice) {
      try {
        await axios.patch(`/api/commissions/commission-invoices/${invoice.id}/payment-status/`, {
          payment_status: 'unpaid',
        })
        this.$bvToast.toast('Facture marquée comme non payée', {
          title: 'Succès',
          variant: 'success',
          solid: true,
        })
        this.loadInvoices()
      } catch (error) {
        this.$bvToast.toast('Erreur lors de la mise à jour', {
          title: 'Erreur',
          variant: 'danger',
          solid: true,
        })
      }
    },
    formatAmount(value) {
      return parseFloat(value).toFixed(3)
    },
    formatDate(dateStr) {
      if (!dateStr) return ''
      return new Date(dateStr).toLocaleDateString('fr-FR')
    },
  },
}
</script>

<style scoped>
.gap-2 {
  gap: 0.5rem;
}

.period-preview {
  background: #f8f9fa;
  padding: 16px;
  border-radius: 8px;
  border: 1px solid #e9ecef;
}

.period-preview h6 {
  margin-bottom: 12px;
  color: #495057;
}

.preview-row {
  display: flex;
  justify-content: space-between;
  padding: 4px 0;
}
</style>
